import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import Sidenav from "examples/Sidenav";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { websiteDetailNav } from "config/sidenavConfig";
import { useMaterialUIController } from "context";

import { listWebsites, getWebsite } from "api/endpoints/websites";

const slugify = (name) => name.toLowerCase().replace(/\s+/g, "-");

// Website-detail layout (/websites/:websitename). Resolves the website, renders
// the route-based context sidenav + a breadcrumb header, and exposes
// { website, websiteId, refreshWebsite } to nested routes via <Outlet context>.
function WebsiteLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { websitename } = useParams();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Resolve the website: prefer the id passed via navigation state; on a deep
  // link / refresh fall back to matching the slug against the website list.
  // Deps are intentionally just [websitename] — depending on location.state
  // would re-fire this on every sibling-route navigation (e.g. clicking the
  // sidenav Settings link), racing the in-flight fetch and surfacing a
  // transient "Failed to load" until the user refreshed.
  useEffect(() => {
    let cancelled = false;

    const resolveWebsite = async () => {
      setLoading(true);
      setError(null);
      try {
        let id = location.state?.websiteId;
        if (!id) {
          const { items } = await listWebsites({ limit: 100 });
          if (cancelled) return;
          id = items.find((w) => slugify(w.name) === websitename)?.website_id;
        }
        if (!id) {
          if (!cancelled) setError("Website not found.");
          return;
        }
        const data = await getWebsite(id);
        if (!cancelled) setWebsite(data);
      } catch {
        if (!cancelled) setError("Failed to load website details.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolveWebsite();
    return () => {
      cancelled = true;
    };
  }, [websitename]);

  const refreshWebsite = useCallback(async () => {
    if (!website) return;
    const data = await getWebsite(website.website_id);
    setWebsite(data);
  }, [website]);

  return (
    <>
      <Sidenav
        color={sidenavColor}
        brandName="VW CMS"
        routes={websiteDetailNav(websitename, location.pathname)}
      />
      <DashboardLayout>
        {loading && (
          <MDBox display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </MDBox>
        )}

        {!loading && error && (
          <MDBox py={10} textAlign="center">
            <MDTypography variant="h5" color="error" mb={1}>
              {error}
            </MDTypography>
            <Link component="button" onClick={() => navigate("/websites")} underline="hover">
              Back to Websites
            </Link>
          </MDBox>
        )}

        {!loading && !error && website && (
          <MDBox py={3}>
            <MDBox mb={2}>
              <Breadcrumbs>
                <Link
                  component="button"
                  onClick={() => navigate("/websites")}
                  underline="hover"
                  color="inherit"
                >
                  Websites
                </Link>
                <MDTypography variant="button" fontWeight="medium" color="text">
                  {website.name}
                </MDTypography>
              </Breadcrumbs>
              <MDTypography variant="h4" fontWeight="bold" mt={0.5}>
                {website.name}
              </MDTypography>
            </MDBox>

            <Outlet context={{ website, websiteId: website.website_id, refreshWebsite }} />
          </MDBox>
        )}
      </DashboardLayout>
    </>
  );
}

export default WebsiteLayout;
